import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppointmentTypesActions } from '../../../constants/actions';
import { AxiosError } from 'axios';
import { AppointmentTypesApi } from 'integration/api/appointmentTypes';
import {
  AppointmentTypeCreationDto,
  AppointmentTypeNotificationConfigRequestDto,
  AppointmentTypeUpdateDto,
  CustomAppointmentTypeNotificationsUpdateDto,
} from '@docbay/schemas';

interface Props {
  page?: number;
  limit?: number;
  specializationIds?: string;
  clinicId?: string;
  professionalIds?: string;
  onlyProfessional?: boolean;
}

/**
 * Fetch appointment types
 *
 * @param params - query parameters (optional)
 * @returns promise containing the fetched appointment types
 */
export const fetchAppointmentTypes = createAsyncThunk(
  AppointmentTypesActions.fetchAppointmentTypes,
  async (params: Props, { rejectWithValue }) => {
    try {
      const { data: responseData } =
        await AppointmentTypesApi.fetchAppointmentTypes(params);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Create a new appointment type
 *
 * @param params - appointment type creation DTO
 * @returns promise containing the created appointment type
 */
export const postAppointmentTypes = createAsyncThunk(
  AppointmentTypesActions.postAppointmentType,
  async (params: AppointmentTypeCreationDto, { rejectWithValue }) => {
    try {
      const { data: responseData } =
        await AppointmentTypesApi.postAppointmentType(params);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetch an appointment type by ID
 *
 * @param params - appointment type ID
 * @returns promise containing the fetched appointment type
 */
export const fetchAppointmentTypeById = createAsyncThunk(
  AppointmentTypesActions.fetchAppointmentTypeById,
  async (params: string, { rejectWithValue }) => {
    try {
      const { data: responseData } =
        await AppointmentTypesApi.fetchAppointmentTypeById(params);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Update an appointment type
 *
 * @param params - object containing the appointment type ID and update DTO
 * @returns promise containing the updated appointment type
 */
export const updateAppointmentType = createAsyncThunk(
  AppointmentTypesActions.updateAppointmentType,
  async (
    params: { id: string; data: AppointmentTypeUpdateDto },
    { rejectWithValue },
  ) => {
    try {
      const { data: responseData } =
        await AppointmentTypesApi.updateAppointmentType(params.id, params.data);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Delete an appointment type
 *
 * @param id - appointment type ID
 * @returns promise containing the deleted appointment type
 */
export const deleteAppointmentTypes = createAsyncThunk(
  AppointmentTypesActions.deleteAppointmentType,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data: responseData } =
        await AppointmentTypesApi.deleteAppointmentType(id);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Update appointment type notifications
 *
 * @param data - object containing the appointment type ID and update DTO for custom notifications
 * @returns promise containing the updated appointment type
 */
export const updateAppointmentTypeNotifications = createAsyncThunk(
  AppointmentTypesActions.updateAppointmentTypeNotifications,
  async (
    data: { id: string; data: CustomAppointmentTypeNotificationsUpdateDto },
    { rejectWithValue },
  ) => {
    try {
      const { data: responseData } =
        await AppointmentTypesApi.updateAppointmentTypeNotifications(
          data.id,
          data.data,
        );
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Change appointment type notification configuration
 *
 * @param data - object containing the appointment type ID and DTO for notification config request
 * @returns promise containing the updated appointment type
 */
export const changeAppointmentTypeNotificationsConfig = createAsyncThunk(
  AppointmentTypesActions.changeAppointmentTypeNotificationsConfig,
  async (
    data: { id: string; data: AppointmentTypeNotificationConfigRequestDto },
    { rejectWithValue },
  ) => {
    try {
      const { data: responseData } =
        await AppointmentTypesApi.changeAppointmentTypeNotificationsConfig(
          data.id,
          data.data,
        );
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);
